import { DateTime } from "luxon";

export const REFERRAL_PROGRAM_ELIGIBLE_STATUSES = new Set([
  "onboarding",
  "finalize",
  "standby",
  "active",
  "inactive",
]);
export const MEMBER_BOOKING_ELIGIBLE_RAW_STATUSES = new Set([
  "active",
  "inactive",
]);
export const LEGACY_INDEFINITE_SUSPENSION_DAYS = 365;
export const DORMANT_HELPER_STATUS_SUFFIX = "_dormant";
export const DECLINED_HELPER_STATUS_SUFFIX = "_declined";

export const normalizeHelperStatus = (value = "") =>
  String(value || "").trim().toLowerCase();

export const normalizeSuspensionType = (value = "") =>
  String(value || "").trim().toLowerCase();

export const buildHelperAccessRecord = ({
  helperData = {},
  helperPrivateData = {},
  helperAdminData = {},
} = {}) => ({
  ...helperData,
  ...helperPrivateData,
  ...helperAdminData,
  bookingBlocked: helperData?.bookingBlocked === true,
  status:
    helperPrivateData?.status ??
    helperData?.status ??
    helperAdminData?.status ??
    "",
  suspendedUntil:
    helperPrivateData?.suspendedUntil ??
    helperData?.suspendedUntil ??
    helperAdminData?.suspendedUntil ??
    null,
  suspension:
    helperPrivateData?.suspension ??
    helperData?.suspension ??
    helperAdminData?.suspension ??
    null,
  statusDormant:
    helperPrivateData?.statusDormant ??
    helperData?.statusDormant ??
    helperAdminData?.statusDormant ??
    null,
});

export const isHelperDormantStatus = (value = "") =>
  normalizeHelperStatus(value).endsWith(DORMANT_HELPER_STATUS_SUFFIX);

export const getHelperBaseStatus = (value = "") => {
  const normalized = normalizeHelperStatus(value);
  if (!isHelperDormantStatus(normalized)) return normalized;
  return normalized.slice(0, -DORMANT_HELPER_STATUS_SUFFIX.length);
};

export const isHelperDeclinedStatus = (value = "") =>
  getHelperBaseStatus(value).endsWith(DECLINED_HELPER_STATUS_SUFFIX);

export const isHelperFailedStatus = (value = "") =>
  getHelperBaseStatus(value).endsWith("_failed");

export const getHelperWorkflowStatus = (value = "") => {
  const baseStatus = getHelperBaseStatus(value);
  if (!baseStatus.endsWith(DECLINED_HELPER_STATUS_SUFFIX)) {
    return baseStatus;
  }

  return baseStatus.slice(0, -DECLINED_HELPER_STATUS_SUFFIX.length);
};

export const buildHelperDormantStateForTransition = ({
  currentStatus = "",
  currentDormantState = null,
  nextStatus = "",
  reactivatedAt = null,
} = {}) => {
  const currentBaseStatus = getHelperBaseStatus(currentStatus);
  const nextBaseStatus = getHelperBaseStatus(nextStatus);
  const wasDormant =
    Boolean(currentDormantState?.active === true) ||
    isHelperDormantStatus(currentStatus);

  if (!wasDormant) {
    return null;
  }

  if (isHelperDeclinedStatus(nextStatus) || isHelperFailedStatus(nextStatus)) {
    return {
      ...(currentDormantState || {}),
      active: true,
      previousStatus: nextBaseStatus,
      reactivatedAt: null,
    };
  }

  return {
    ...(currentDormantState || {}),
    active: false,
    previousStatus: currentBaseStatus,
    reactivatedAt,
  };
};

export const resolveHelperStatusForTransition = ({
  currentStatus = "",
  currentDormantState = null,
  nextStatus = "",
} = {}) => {
  const nextBaseStatus = getHelperBaseStatus(nextStatus);
  if (!nextBaseStatus) return nextBaseStatus;

  const nextDormantState = buildHelperDormantStateForTransition({
    currentStatus,
    currentDormantState,
    nextStatus: nextBaseStatus,
  });

  if (nextDormantState?.active === true) {
    return `${nextBaseStatus}${DORMANT_HELPER_STATUS_SUFFIX}`;
  }

  return nextBaseStatus;
};

export const toDateTime = (value = null) => {
  if (!value) return null;
  if (DateTime.isDateTime(value)) return value;
  if (typeof value?.toDate === "function") {
    return DateTime.fromJSDate(value.toDate());
  }
  if (value instanceof Date) {
    return DateTime.fromJSDate(value);
  }
  if (typeof value?.toMillis === "function") {
    return DateTime.fromMillis(value.toMillis());
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return DateTime.fromJSDate(parsed);
};

export const getHelperSuspensionState = (helper = {}) => {
  const suspension = helper?.suspension || helper?.admin?.suspension || {};
  return {
    status: normalizeHelperStatus(helper?.status || ""),
    suspensionType: normalizeSuspensionType(suspension?.type || ""),
    suspendedUntil: toDateTime(helper?.suspendedUntil || suspension?.until || null),
    previousStatus: normalizeHelperStatus(suspension?.previousStatus || ""),
  };
};

export const isHelperDormant = (helper = {}) =>
  Boolean(helper?.statusDormant?.active === true) ||
  isHelperDormantStatus(helper?.status || "");

export const isLegacyIndefiniteHelperSuspension = (
  helper = {},
  now = DateTime.now(),
) => {
  const state = getHelperSuspensionState(helper);
  if (state.status === "suspended" || state.suspensionType === "indefinite") {
    return false;
  }
  return Boolean(
    state.suspendedUntil &&
      state.suspendedUntil.isValid &&
      state.suspendedUntil > now.plus({ days: LEGACY_INDEFINITE_SUSPENSION_DAYS }),
  );
};

export const isHelperIndefinitelySuspended = (
  helper = {},
  now = DateTime.now(),
) => {
  const state = getHelperSuspensionState(helper);
  return (
    state.status === "suspended" ||
    state.suspensionType === "indefinite" ||
    isLegacyIndefiniteHelperSuspension(helper, now)
  );
};

export const isHelperTemporarilySuspended = (
  helper = {},
  now = DateTime.now(),
) => {
  const state = getHelperSuspensionState(helper);
  if (isHelperIndefinitelySuspended(helper, now)) {
    return false;
  }
  return Boolean(
    state.suspendedUntil &&
      state.suspendedUntil.isValid &&
      state.suspendedUntil > now,
  );
};

export const isHelperSuspended = (helper = {}, now = DateTime.now()) =>
  isHelperIndefinitelySuspended(helper, now) ||
  isHelperTemporarilySuspended(helper, now);

export const getHelperDashboardStatus = (helper = {}) => {
  const state = getHelperSuspensionState(helper);
  if (state.status === "suspended" && state.previousStatus) {
    return getHelperBaseStatus(state.previousStatus);
  }
  return getHelperBaseStatus(state.status);
};

export const canHelperAccessReferralProgram = (
  helper = {},
  now = DateTime.now(),
) => {
  const dashboardStatus = getHelperDashboardStatus(helper);
  return (
    !isHelperDormant(helper) &&
    !isHelperSuspended(helper, now) &&
    REFERRAL_PROGRAM_ELIGIBLE_STATUSES.has(dashboardStatus)
  );
};

export const canHelperSeeAreaStatsOnHome = (
  helper = {},
  now = DateTime.now(),
) => {
  const dashboardStatus = getHelperDashboardStatus(helper);
  return (
    !isHelperSuspended(helper, now) &&
    /^(active|onboarding|finalize|inactive)/.test(dashboardStatus) &&
    !/_(hold|declined|failed)$/.test(dashboardStatus) &&
    Boolean(String(helper?.currentArea?.key || "").trim())
  );
};

export const canHelperBookVisitsForMembers = (
  helper = {},
  now = DateTime.now(),
) => {
  const rawStatus = normalizeHelperStatus(helper?.status || "");
  return (
    helper?.bookingBlocked !== true &&
    !isHelperDormant(helper) &&
    !isHelperSuspended(helper, now) &&
    MEMBER_BOOKING_ELIGIBLE_RAW_STATUSES.has(rawStatus)
  );
};

export const canHelperAccessMemberDirectory = (helper = {}) => {
  const dashboardStatus = getHelperDashboardStatus(helper);
  return (
    !isHelperDormant(helper) &&
    !isHelperIndefinitelySuspended(helper) &&
    dashboardStatus === "active"
  );
};

export const canHelperAccessMemberMessaging = (helper = {}) => {
  const dashboardStatus = getHelperDashboardStatus(helper);
  return (
    !isHelperDormant(helper) &&
    !isHelperIndefinitelySuspended(helper) &&
    dashboardStatus === "active"
  );
};
